/**
 * Main App Component
 * Provana Work Allocation System
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, Typography, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { LoaderProvider, GlobalLoader } from './components/common/GlobalLoader';
import { UserRole } from './types/User.types';
import { typography } from './theme/typography';
import { tokens } from './theme/tokens';

// Layout (not lazy - needed immediately)
import Layout from './components/common/Layout';

const IS_MOCK_MODE = import.meta.env.VITE_AZURE_CLIENT_ID === 'mock-client-id-for-testing';

// Lazy-loaded Pages (code splitting for better performance)
const Login = lazy(() => import('./pages/Login'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Upload = lazy(() => import('./pages/Upload'));
const AllocationMatrix = lazy(() => import('./pages/AllocationMatrix'));
const ReviewApprove = lazy(() => import('./pages/ReviewApprove'));
const ApprovalsUnified = lazy(() => import('./pages/ApprovalsUnified'));
const History = lazy(() => import('./pages/History'));
const TeamIntelligence = lazy(() => import('./pages/TeamIntelligence'));

// Phase 2 — conditionally loaded based on feature flags
const FEATURE_MY_WORK  = import.meta.env.VITE_FEATURE_MY_WORK  === 'true';
const FEATURE_REPORTS  = import.meta.env.VITE_FEATURE_REPORTS  === 'true';
const MyWork  = FEATURE_MY_WORK  ? lazy(() => import('./pages/MyWork'))  : null;
const Reports = FEATURE_REPORTS  ? lazy(() => import('./pages/Reports')) : null;

// Page Loading Fallback Component
const PageLoader = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      width: '100%',
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

// Provana theme configuration - Updated May 2026
const theme = createTheme({
  palette: {
    primary: {
      main: tokens.colors.primary.main,
      light: tokens.colors.primary.light,
      dark: tokens.colors.primary.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.colors.secondary.main,
      light: tokens.colors.secondary.light,
      dark: tokens.colors.secondary.dark,
      contrastText: '#FFFFFF',
    },
    error: {
      main: tokens.colors.semantic.error,
      light: tokens.colors.semantic.errorLight,
      dark: tokens.colors.semantic.errorDark,
    },
    warning: {
      main: tokens.colors.semantic.warning,
      light: tokens.colors.semantic.warningLight,
      dark: tokens.colors.semantic.warningDark,
    },
    success: {
      main: tokens.colors.semantic.success,
      light: tokens.colors.semantic.successLight,
      dark: tokens.colors.semantic.successDark,
    },
    info: {
      main: tokens.colors.semantic.info,
      light: tokens.colors.semantic.infoLight,
      dark: tokens.colors.semantic.infoDark,
    },
    background: {
      default: tokens.colors.neutral[50],
      paper: tokens.colors.surface.base,
    },
    text: {
      primary: tokens.colors.text.primary,
      secondary: tokens.colors.text.secondary,
      disabled: tokens.colors.text.disabled,
    },
  },
  typography: typography,
  shape: {
    borderRadius: tokens.borderRadius.lg,
  },
  shadows: [
    'none',
    tokens.shadows.xs,
    tokens.shadows.sm,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: tokens.borderRadius.md,
          padding: '10px 20px',
          fontSize: 14,
          letterSpacing: '0.01em',
          boxShadow: 'none',
          transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${tokens.colors.primary.main} 0%, ${tokens.colors.primary[600]} 100%)`,
          boxShadow: `0 1px 3px rgba(139, 92, 246, 0.2)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${tokens.colors.primary[600]} 0%, ${tokens.colors.primary[700]} 100%)`,
            boxShadow: `0 4px 12px rgba(139, 92, 246, 0.3)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${tokens.colors.secondary.main} 0%, ${tokens.colors.secondary.dark} 100%)`,
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: tokens.colors.primary[50],
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.sm,
          border: `1px solid ${tokens.colors.neutral[200]}`,
          transition: `all ${tokens.transitions.duration.normal} ${tokens.transitions.easing.standard}`,
          '&:hover': {
            boxShadow: tokens.shadows.md,
            borderColor: tokens.colors.neutral[300],
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: tokens.borderRadius.lg,
        },
        elevation1: {
          boxShadow: tokens.shadows.sm,
        },
        elevation2: {
          boxShadow: tokens.shadows.md,
        },
        elevation3: {
          boxShadow: tokens.shadows.lg,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: tokens.colors.surface.sunken,
            borderRadius: tokens.borderRadius.md,
            transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
            '&:hover': {
              backgroundColor: tokens.colors.neutral[100],
            },
            '&.Mui-focused': {
              backgroundColor: tokens.colors.surface.base,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.colors.primary.main,
                borderWidth: '1.5px',
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.colors.neutral[100]}`,
        },
        head: {
          backgroundColor: tokens.colors.neutral[50],
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: tokens.colors.neutral[700],
          borderBottom: `2px solid ${tokens.colors.neutral[200]}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
          '&:hover': {
            backgroundColor: tokens.colors.neutral[50],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: 12,
          borderRadius: tokens.borderRadius.md,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.standard}`,
          '&:hover': {
            backgroundColor: tokens.colors.neutral[100],
          },
        },
      },
    },
  },
});

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Temporary placeholder components
const AdminPlaceholder = () => (
  <Layout>
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Admin Dashboard</Typography>
      <Typography>Coming Soon</Typography>
    </Box>
  </Layout>
);

const ConfigPlaceholder = () => (
  <Layout>
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Configuration</Typography>
      <Typography>Coming Soon</Typography>
    </Box>
  </Layout>
);

const IntegrationsPlaceholder = () => (
  <Layout>
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Integrations</Typography>
      <Typography>Coming Soon</Typography>
    </Box>
  </Layout>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LoaderProvider>
            <GlobalLoader />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AuthProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Protected Routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN]}>
                          <Layout>
                            <Dashboard />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/upload"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.ADMIN]}>
                          <Layout>
                            <Upload />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/allocation/matrix"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.ADMIN]}>
                          <Layout>
                            <AllocationMatrix />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/allocation/approvals"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN]}>
                          <Layout>
                            <ApprovalsUnified />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/allocation/review"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN]}>
                          <Layout>
                            <ReviewApprove />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/history"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN]}>
                          <Layout>
                            <History />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/team"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN]}>
                          <Layout>
                            <TeamIntelligence />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Phase 2 — feature-flag-gated routes */}
                    {FEATURE_MY_WORK && MyWork && (
                      <Route
                        path="/employee/my-work"
                        element={
                          <ProtectedRoute allowedRoles={[UserRole.EMPLOYEE, UserRole.SUPERVISOR, UserRole.ADMIN]}>
                            <Layout>
                              <MyWork />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                    )}

                    {FEATURE_REPORTS && Reports && (
                      <Route
                        path="/reports"
                        element={
                          <ProtectedRoute allowedRoles={[UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN, UserRole.OPEX]}>
                            <Layout>
                              <Reports />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                    )}

                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                          <AdminPlaceholder />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/config"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                          <ConfigPlaceholder />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/integrations"
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                          <IntegrationsPlaceholder />
                        </ProtectedRoute>
                      }
                    />

                    {/* Root path renders Login — must NOT redirect here because
                         Microsoft's OAuth redirect lands at '/' with auth code
                         query params; a Navigate would strip those before MSAL
                         can call handleRedirectPromise(). */}
                    <Route path="/" element={<Login />} />

                    {/* 404 Not Found */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </LoaderProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
